import { Component, OnInit } from '@angular/core';

import { InboxService } from '../../../../services/inbox/inbox.service';
import { StaffNoteService } from '../../../../services/staff-note/staff-note.service';
import { ListResponse } from '../../../../models/list-response';
import { StaffNote } from '../../../../models/staff-note';
import { AccountService } from '../../../../services/account/account.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AddStaffNoteModalComponent } from './modals/add-staff-note-modal/add-staff-note-modal.component';
import { EditStaffNoteModalComponent } from './modals/edit-staff-note-modal/edit-staff-note-modal.component';
import { dateStringFromISO8601, getISO8601DateString, timeStringFromISO8601 } from '../../../../utils';
import { ScheduledEvent } from '../../../../models/scheduled-event';
import { ScheduledEventService } from '../../../../services/scheduled-event/scheduled-event.service';
import { OrganizationConfigService } from '../../../../services/organization-config/organization-config.service';
import { CheckInStats } from '../../../../models/check-in';
import { CheckInService } from '../../../../services/check-in/check-in.service';
import { CapitanLocation } from '../../../../models/location';
import { LocationService } from '../../../../services/location/location.service';
import { MyAccount } from '../../../../models/my-account';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {

  public location: CapitanLocation;
  public scheduledEventsList: ScheduledEvent[];
  public staffNotesList: ListResponse<StaffNote>;
  public dismissedNotesList: ListResponse<StaffNote>;
  public checkInStats: CheckInStats;

  public loadingLocation = true;
  public loadingScheduledEvents = true;
  public loadingStaffNotes = true;
  public loadingDismissedNotes = true;
  public loadingCheckInStats = true;

  public showDismissedNotes = false;
  public todaysEventsLocationId: number;

  public serverError: number = null;

  public myAccount: MyAccount;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;

  constructor(
    public inboxService: InboxService,
    public scheduledEventService: ScheduledEventService,
    public staffNoteService: StaffNoteService,
    public orgConfigService: OrganizationConfigService,
    public checkInService: CheckInService,
    public locationService: LocationService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.myAccount = this.accountService.getMyAccount();

    this.todaysEventsLocationId = this.myAccount.default_gym_id;

    this.loadLocation();
    this.loadStaffNotes(true);
    this.loadDismissedNotes(true);
    this.loadCheckInStats();
    this.loadScheduledEvents();
  }

  public loadLocation() {
    this.loadingLocation = true;

    this.locationService
      .getLocation(this.accountService.getMyAccount().default_gym_id)
      .then(location => {
        this.location = location;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocation = false; });
  }

  public loadScheduledEvents() {
    this.loadingScheduledEvents = true;

    this.scheduledEventService
      .getAllScheduledEvents(
        getISO8601DateString(new Date()),
        {location_id: this.todaysEventsLocationId}
      )
      .then(response => {
        this.scheduledEventsList = response.filter(se => !se.is_cancelled);
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingScheduledEvents = false; });
  }

  public loadStaffNotes(setLoadState: boolean) {
    this.loadingStaffNotes = setLoadState;

    this.staffNoteService
      .getStaffNotes(1, 10, {is_dismissed: false})
      .then(response => { this.staffNotesList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingStaffNotes = false; });
  }

  public loadDismissedNotes(setLoadState: boolean) {
    this.loadingDismissedNotes = setLoadState;

    this.staffNoteService
      .getStaffNotes(1, 10, {is_dismissed: true})
      .then(response => { this.dismissedNotesList = response; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingDismissedNotes = false; });
  }

  public loadCheckInStats() {
    this.loadingCheckInStats = true;

    this.checkInService
      .getCheckInStats(
        this.accountService.getMyAccount().default_gym_id,
        false
      )
      .then(checkInStats => { this.checkInStats = checkInStats; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCheckInStats = false; });
  }

  public locationIsOverCapacity() {
    if (!this.checkInStats || !this.location) { return false; }

    return (this.checkInStats.currently_checked_in_count > this.location.capacity);
  }

  public scheduledEventsPresent(): boolean {
    return (
      this.scheduledEventsList &&
      (this.scheduledEventsList.length > 0)
    );
  }

  public staffNotesPresent(): boolean {
    return (
      this.staffNotesList &&
      this.staffNotesList.results &&
      (this.staffNotesList.results.length > 0)
    );
  }

  public dismissedNotesPresent(): boolean {
    return (
      this.dismissedNotesList &&
      this.dismissedNotesList.results &&
      (this.dismissedNotesList.results.length > 0)
    );
  }

  public getEventLink(scheduledEvent: ScheduledEvent) {
    let urlBase: string;
    if (scheduledEvent.has_ended) {
      urlBase = '/events/scheduled/past/' + getISO8601DateString(new Date());
    } else if (!scheduledEvent.has_ended && scheduledEvent.has_started) {
      urlBase = '/events/scheduled/now';
    } else {
      urlBase = '/events/scheduled/upcoming/' + getISO8601DateString(new Date());
    }
    return urlBase  + '/event/' + scheduledEvent.event_part_id.toString();
  }

  public openAddNoteModal() {
    const modalRef = this.modalService.open(AddStaffNoteModalComponent);
    modalRef.componentInstance.savedCallback = () => {
      this.loadStaffNotes(true);
    };
  }

  public openEditNoteModal(staffNote: StaffNote) {
    // Create a copy of the staff note so that the note's info is only updated
    // in the dashboard after it is saved to the API.
    const staffNoteInput: StaffNote = {...staffNote};

    const modalRef = this.modalService.open(EditStaffNoteModalComponent);
    modalRef.componentInstance.staffNote = staffNoteInput;
    modalRef.componentInstance.savedCallback = () => {
      this.loadStaffNotes(true);
    };
  }

  public dismissNote(note: StaffNote) {
    note.is_dismissed = true;
    this.staffNoteService
      .updateStaffNote(note)
      .then(updatedNote => {
        this.loadStaffNotes(false);
        this.loadDismissedNotes(false);
      });
  }

  public undoDismissNote(note: StaffNote) {
    note.is_dismissed = false;
    this.staffNoteService
      .updateStaffNote(note)
      .then(updatedNote => {
        this.loadStaffNotes(false);
        this.loadDismissedNotes(false);
      });
  }
}
